/**
 * Venta de prueba del asistente de configuración (paso "Venta").
 *
 * Es una venta real: se cobra en el POS y queda registrada como cualquier venta del tótem. Replica
 * el flujo de pago de useShoppingCart.checkoutAndWaitStatusViaAPI (y la impresión de
 * CheckoutModal) sin depender del estado de React del tótem, que aún no está configurado.
 */
import { collection, doc, getDocFromServer, getDocsFromServer, onSnapshot, query, updateDoc, where, Timestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { getProductInfo } from "./productHelpers.js";
import { ReceiptGenerator } from "./ReceiptGenerator.js";

const CHECKOUT_URL = "https://getcheckout-x3herayjcq-uc.a.run.app";
const CHECKOUT_STATUS_URL = "https://checkcheckoutstatus-x3herayjcq-uc.a.run.app";
const TRANSBANK_SALE_URL = "https://localhost:5001/sale";

/** Estados de checkout de una venta pagada (los mismos que usa CheckoutModal para imprimir). */
const PAID_STATUSES = ["paid", "processed", "approved"];
/** Estados en que el pago POSAPI sigue en curso en el terminal. */
const PENDING_STATUSES = ["created", "at_terminal"];
/** Límite de espera del pago en el terminal (el cliente puede demorar en pasar la tarjeta). */
const PAYMENT_TIMEOUT = 3 * 60 * 1000;

const clientPath = (env, client) => `kiosk/${env}/client/${client}`;

/**
 * Busca el producto de menor precio para la tienda de la instancia, esté o no disponible. Se
 * descartan los que exigen elegir modificadores (la venta de prueba no los selecciona).
 *
 * @returns {Promise<{ok: true, data: {product: Object, price: number, store: Object|null}} | {ok: false, error: 'no_product' | 'network'}>}
 */
export async function findCheapestProduct(env, client, storeId) {
  try {
    const base = clientPath(env, client);
    const [itemsSnap, modifiersSnap, storeSnap] = await Promise.all([
      getDocsFromServer(collection(db, `${base}/items`)),
      getDocsFromServer(collection(db, `${base}/modifiers`)),
      storeId ? getDocFromServer(doc(db, `${base}/stores/${storeId}`)) : Promise.resolve(null),
    ]);
    const store = storeSnap?.exists() ? { id: storeSnap.id, ...storeSnap.data() } : null;
    // Igual que filteredStoreIds en App: la tienda de la instancia y sus tiendas de respaldo.
    const storeIds = [storeId, ...(store?.backup_stores || []).filter((id) => id !== storeId)].filter(Boolean);
    const requiredModifierIds = new Set(modifiersSnap.docs.filter((m) => (m.data().min || 0) >= 1).map((m) => m.id));

    // Se toma el de menor precio aunque no esté disponible (`is_available: false`): así un producto
    // de prueba de bajo precio puede usarse aquí sin aparecer en el menú del tótem.
    let cheapest = null;
    itemsSnap.docs.forEach((snapshot) => {
      const product = { id: snapshot.id, ...snapshot.data() };
      if ((product.modifiers || []).some((id) => requiredModifierIds.has(id))) return;
      const { commonPrice } = getProductInfo(product, storeIds);
      if (!(commonPrice > 0)) return;
      if (!cheapest || commonPrice < cheapest.price) cheapest = { product, price: commonPrice };
    });

    if (!cheapest) return { ok: false, error: "no_product" };
    return { ok: true, data: { ...cheapest, store } };
  } catch (error) {
    console.error("[TestSale] Error finding product:", error);
    return { ok: false, error: "network" };
  }
}

/**
 * Cobra la venta de prueba con el método de pago indicado.
 *
 * @param {Object} params
 * @param {(status: 'creating' | 'at_terminal' | 'paid') => void} [params.onStatus]
 * @returns {Promise<{ok: true, cartId: string, cart: Object} | {ok: false, error: 'checkout' | 'payment' | 'timeout', detail?: string|null}>}
 */
export async function runTestSale({ env, client, instanceId, storeId, method, product, price, store, clientData, terminalData, onStatus }) {
  const details = [{ unique_id: `${product.id}-${Date.now()}`, id: product.id, modifiers: {}, quantity: 1 }];
  onStatus?.("creating");

  if (method.type === "POS") return runUSBSale({ env, client, instanceId, storeId, method, details, price, store, clientData, terminalData, onStatus });

  const checkout = await createCheckout({ env, client, instanceId, storeId, method, details, externalSaleFlow: false });
  if (!checkout.ok) return checkout;
  let cart = checkout.data;
  const startedAt = Date.now();
  while (PENDING_STATUSES.includes(cart.checkout_status)) {
    onStatus?.("at_terminal");
    if (Date.now() - startedAt > PAYMENT_TIMEOUT) return { ok: false, error: "timeout" };
    await new Promise((resolve) => setTimeout(resolve, 1000));
    try {
      cart = await checkCheckoutStatus(env, client, cart.id);
    } catch (error) {
      console.error("[TestSale] Error checking checkout status:", error);
    }
  }
  if (!PAID_STATUSES.includes(cart.checkout_status)) {
    const detail = cart.payment_summary?.response?.responseMessage || cart.checkout_summary?.message || cart.checkout_status || null;
    return { ok: false, error: "payment", detail };
  }
  onStatus?.("paid");
  return { ok: true, cartId: cart.id, cart };
}

// Transbank: la venta se crea en el backend (flujo externo) y se cobra en el POS por el servicio
// local; luego se registra el resultado en el carro, igual que useShoppingCart.
async function runUSBSale({ env, client, instanceId, storeId, method, details, price, store, clientData, terminalData, onStatus }) {
  const checkout = await createCheckout({ env, client, instanceId, storeId, method, details, externalSaleFlow: true });
  if (!checkout.ok) return checkout;
  const cartId = checkout.data.id;
  const numericId = checkout.data.numeric_id;
  if (!cartId || !numericId) return { ok: false, error: "checkout", detail: null };

  onStatus?.("at_terminal");
  let sale;
  try {
    const response = await fetch(TRANSBANK_SALE_URL, {
      method: "POST", mode: "cors", cache: "no-cache", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ Amount: price, Ticket: numericId.toString(), Print: 0 }),
    });
    sale = await response.json();
  } catch (error) {
    sale = { error: error.message || "No se pudo conectar con el POS." };
  }
  const paid = sale && sale.responseCode === 0;
  const amount = sale?.amount || null;
  const now = Timestamp.now();
  const update = {
    payment_id: numericId,
    payment_summary: sale || null,
    external_order_id: numericId,
    checkout_summary: sale || null,
    checkout_status: paid ? "processed" : "canceled",
    processed_sale: paid,
    checkout_was_printed: false,
    checkout_terminal: terminalData || null,
    updated_at: now,
    ticket_data: {
      aid: sale?.authorizationCode || null,
      amount,
      branch_address: store?.address || null,
      branch_district: null,
      branch_name: store?.name || null,
      card_brand: sale?.cardBrand || null,
      card_type: sale?.cardType || null,
      commerce_name: clientData?.name || null,
      commerce_tin: clientData?.pin || null,
      iva_amount: amount && clientData?.tax ? Math.round(amount * clientData.tax) : null,
      last_digits: sale?.last4Digits || null,
      operation_id: sale?.ticket || null,
      terminal_id: sale?.terminalId || null,
      ticket_date: now,
    },
  };
  try {
    await updateDoc(doc(db, `${clientPath(env, client)}/carts/${cartId}`), update);
  } catch (error) {
    console.error("[TestSale] Error saving Transbank result:", error);
  }
  if (!paid) return { ok: false, error: "payment", detail: sale?.responseMessage || sale?.error || null };
  onStatus?.("paid");
  return { ok: true, cartId, cart: { ...checkout.data, ...update } };
}

async function createCheckout({ env, client, instanceId, storeId, method, details, externalSaleFlow }) {
  try {
    const response = await fetch(`${CHECKOUT_URL}?environment=${env}&client=${client}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ details, store_id: storeId, instance_id: instanceId, external_sale_flow: externalSaleFlow, is_client: false, payment_option: method, source: "kiosk" }),
    });
    if (!response.ok) {
      const text = await response.text();
      let detail = text;
      try {
        detail = JSON.parse(text)?.message || text;
      } catch {
        // No es JSON; se muestra el texto tal cual.
      }
      return { ok: false, error: "checkout", detail: detail || null };
    }
    return { ok: true, data: await response.json() };
  } catch (error) {
    console.error("[TestSale] Checkout error:", error);
    return { ok: false, error: "checkout", detail: error.message || null };
  }
}

async function checkCheckoutStatus(env, client, cartId) {
  const response = await fetch(`${CHECKOUT_STATUS_URL}?environment=${env}&client=${client}&cart_id=${cartId}`, { method: "GET", headers: { "Content-Type": "application/json" } });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
}

/**
 * Marca la venta para impresión (con los datos de la boleta), igual que CheckoutModal; el backend
 * genera los documentos y los encola para Printer Bridge.
 */
export async function requestTestSalePrint(env, client, cartId, clientData, cart) {
  const data = { ready_to_print: true };
  try {
    const generation = await ReceiptGenerator.generateReceipt(clientData, cart);
    if (generation?.id) {
      data["ticket_data.security_id"] = generation.id;
      if (generation.code) data["ticket_data.security_code"] = generation.code;
      const consolidation = await ReceiptGenerator.consolidateReceipt(generation.id);
      if (consolidation?.code) data["ticket_data.security_content"] = consolidation.code;
    }
  } catch (error) {
    console.error("[TestSale] Receipt generation error:", error);
  }
  await updateDoc(doc(db, `${clientPath(env, client)}/carts/${cartId}`), data);
}

/**
 * Escucha la cola de impresión de la venta y notifica su estado ('pending', 'printed', ...).
 *
 * @returns {() => void} Cancela la escucha.
 */
export function watchTestSalePrint(env, client, cartId, onStatus) {
  const q = query(collection(db, `${clientPath(env, client)}/queues`), where("cart_id", "==", cartId));
  return onSnapshot(q, (snapshot) => {
    const statuses = snapshot.docs.map((d) => d.data().status);
    if (statuses.length === 0) return;
    onStatus(statuses.every((s) => s === "printed") ? "printed" : statuses.includes("canceled") ? "canceled" : "pending");
  }, (error) => console.error("[TestSale] Error listening to print queue:", error));
}
